// VRG-156 — small, non-interactive preview of a placed customization face
// (front / back), shown beside the "Customized" add-on button once a design is
// saved. Mirrors the TagCanvasEditor's placed-logo render at thumbnail scale so
// the buyer sees exactly what they positioned without reopening the modal.

import React from 'react'

import { Box, Tooltip, useTheme } from '@mui/material'

import type { LogoPlacement } from './TagCanvasEditor'

interface TagFacePreviewProps {
  shape: 'round' | 'rect'
  /** width / height aspect of the tag face (1 = square/round). */
  aspect: number
  tagColor: string
  logoDataUrl: string | null
  placement: LogoPlacement | null
  /** Woven embroidered look — emboss the logo so it reads as raised thread. */
  embroidered?: boolean
  /** Tooltip label, e.g. "Front" / "Back". */
  label?: string
  /** Rendered height in px (width derives from aspect). Default 40. */
  size?: number
}

export const TagFacePreview = ({
  shape,
  aspect,
  tagColor,
  logoDataUrl,
  placement,
  embroidered = false,
  label,
  size = 40,
}: TagFacePreviewProps) => {
  const theme = useTheme()
  const isRound = shape === 'round'
  const height = size
  const width = Math.round(size * (aspect > 0 ? aspect : 1))

  const face = (
    <Box
      sx={{
        position: 'relative',
        width,
        height,
        flexShrink: 0,
        backgroundColor: tagColor,
        borderRadius: isRound ? '50%' : 1,
        border: `1px solid ${theme.palette.primary[200]}`,
        overflow: 'hidden',
      }}
    >
      {logoDataUrl && placement && (
        <Box
          sx={{
            position: 'absolute',
            left: `${placement.xPct}%`,
            top: `${placement.yPct}%`,
            width: `${placement.scalePct}%`,
            transform: `translate(-50%, -50%) rotate(${placement.rotation}deg)`,
          }}
        >
          <Box
            component="img"
            src={logoDataUrl}
            alt={label ?? 'custom logo'}
            draggable={false}
            sx={{
              width: '100%',
              display: 'block',
              // Lightweight version of the editor's emboss — the fine stitch mask
              // doesn't read at 40px, but the raised-thread emboss + saturation does.
              filter: embroidered
                ? 'drop-shadow(0.4px 0.4px 0.2px rgba(0,0,0,0.5)) drop-shadow(-0.3px -0.3px 0.2px rgba(255,255,255,0.55)) saturate(1.2)'
                : undefined,
            }}
          />
        </Box>
      )}
    </Box>
  )

  return label ? (
    <Tooltip title={label} placement="top">
      {face}
    </Tooltip>
  ) : (
    face
  )
}
